<template>
  <router-view />

  <onboarding-gate
    v-model="showOnboardingGate"
    @start-setup="handleStartSetup"
    @request-logout="handleLogout"
  />
</template>
<script>
import { defineComponent } from "vue";
import { useQuasar } from "quasar";
import iconSet from "src/icons/iconset.js";
import { mapState } from "vuex";
import OnboardingGate from "components/OnboardingGate.vue";
import { onboardingTestActive } from "src/utils/onboardingOverride";

export default {
  name: "App",
  components: {
    OnboardingGate,
  },
  setup() {
    const $q = useQuasar();
    $q.iconSet.set(iconSet);
  },
  data() {
    return {
      isAppReady: false,
      gateStarted: false,
      membershipDataLoaded: false,
      forceCloseGate: false,
    };
  },
  computed: {
    ...mapState("pricingWizard", ["companyPlans"]),
    userData() {
      return this.$store.state.auth.user;
    },
    companyFromAuth() {
      return this.$store.state.auth?.user?.company ?? null;
    },
    companyFromData() {
      return (
        this.$store.state.data?.company || this.$store.state.current?.company
      );
    },
    companyMembershipPlan() {
      return (
        this.companyFromAuth?.membership_plan ||
        this.companyFromData?.membership_plan ||
        null
      );
    },
    hasMembership() {
      const cps = this.companyPlans || [];
      const hasFromPlans = cps.some(
        (p) => (p.type?.code || p.type_code) === "membership"
      );

      const hasFromCompany =
        !!this.companyMembershipPlan?.id &&
        this.companyFromData?.membership_payment_failed !== true;

      return hasFromPlans || hasFromCompany;
    },
    isOnPricingPage() {
      return this.$route.path.includes("/pricing-plans");
    },
    shouldShowOnboardingGate() {
      if (
        !this.isAppReady ||
        !this.membershipDataLoaded ||
        !this.userData?.id
      ) {
        return false;
      }

      if (this.isOnPricingPage) return false;

      const id = Number(this.companyFromData?.id ?? this.companyFromAuth?.id);
      if (id === 159) {
        if (localStorage.getItem("onboarding_completed") !== "true") {
          localStorage.setItem("onboarding_completed", "true");
        }
        return false;
      }

      // Trust localStorage if it says completed - don't re-validate
      const completed = localStorage.getItem("onboarding_completed") === "true";
      if (completed) {
        return false;
      }

      // Only show gate if user opted in OR genuinely has no membership
      const optedIn = onboardingTestActive(this.$route);
      return optedIn || !this.hasMembership;
    },
    showOnboardingGate: {
      get() {
        if (this.forceCloseGate) return false;
        return this.shouldShowOnboardingGate;
      },
      set() {
        // Handled by actions, not directly settable
      },
    },
    shouldShowLimitedBanner() {
      return (
        this.isLimitedAccessMode &&
        !this.limitedBannerDismissed &&
        !this.isOnPricingPage &&
        this.userData?.id
      );
    },
  },
  methods: {
    handleLogout() {
      this.forceCloseGate = true;
      this.$router.push("/logout");
    },
    handleAndroidResizeEvent() {
      //When android opens keyboard on a focused input, input may become out of viewport.
      //This handling will move a scroll position to a focused input.
      let focusElement = document.activeElement;
      if (
        focusElement &&
        ["input", "textarea", "select"].indexOf(
          focusElement.tagName.toLowerCase()
        ) >= 0
      ) {
        //If input is a part of Quasar field, then focus on it.
        let qField = focusElement.closest(".q-field");
        if (qField) {
          focusElement = qField;
        }
        setTimeout(() => {
          focusElement.scrollIntoView({ block: "end", behavior: "smooth" });
        }, 200); //delay to be sure android keyboard is fully rendered.
      }
    },
    async handleStartSetup() {
      this.gateStarted = true;

      await this.$router.push({
        path: "/my/company/pricing-plans",
        query: { onboarding: "1" }, // carry the opt-in flag forward
      });
    },
    async initializeApp() {
      try {
        if (this.userData?.id) {
          await this.$store.dispatch("pricingWizard/hydratePricing");
          this.membershipDataLoaded = true;
        }
        this.isAppReady = true;
      } catch (error) {
        console.error("Failed to load pricing data:", error);
        this.membershipDataLoaded = true;
        this.isAppReady = true;
      }
    },
    // loadUserDataToBugPilot() {
    //   if (typeof window.Bugpilot !== "undefined") {
    //     window.Bugpilot.identify({
    //       id: this.userData.login, // Required
    //       email: this.userData.email, // Required for help desk integrations
    //     });
    //   }
    // },
  },
  watch: {
    userData(newData, oldData) {
      if (
        newData?.login !== oldData?.login ||
        newData?.email !== oldData?.email
      ) {
        //this.loadUserDataToBugPilot();
      }
      if (newData?.id && !oldData?.id) {
        this.initializeApp();
      }
    },
  },
  async mounted() {
    if (process.env.APP_TYPE === "native") {
      //Close splash screen (mobile native apps).
      this.$appNative?.closeSplashScreen();
    }
    if (this.$q.platform.is.android) {
      window.addEventListener("resize", this.handleAndroidResizeEvent);
    }
    // if (this.userData?.id) {
    //   this.loadUserDataToBugPilot();
    // }
    if (this.userData?.id) {
      await this.initializeApp();
    } else {
      this.isAppReady = true;
      this.membershipDataLoaded = true;
    }
  },
  beforeUnmount() {
    if (this.$q.platform.is.android) {
      window.removeEventListener("resize", this.handleAndroidResizeEvent);
    }
  },
};
</script>
